import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useAuth } from '@/contexts/AuthContext';
import { useModalQueue } from '@/contexts/ModalQueueContext';
import { apiClient } from '@/lib/api-client';

interface PWAInstallStatus {
  enabled: boolean;
  gcAmount: number;
  scAmount: number;
  pwaInstalled: boolean;
  rewardClaimed: boolean;
  canClaim: boolean;
}

interface PWAInstallContextType {
  // From usePWAInstall hook
  isInstallable: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isStandalone: boolean;
  hasNativePrompt: boolean;
  promptInstall: () => Promise<boolean>;
  
  // Reward status
  status: PWAInstallStatus | null;
  loading: boolean;
  
  // Actions
  refreshStatus: () => Promise<void>;
  markInstalled: () => Promise<void>;
  claimReward: () => Promise<{ gcAmount: number; scAmount: number } | null>;
  
  // Modal control
  showInstallPrompt: boolean;
  dismissInstallPrompt: () => void;
  triggerInstallPrompt: () => void;
}

const PWAInstallContext = createContext<PWAInstallContextType | null>(null);

export const usePWAInstallContext = () => {
  const ctx = useContext(PWAInstallContext);
  if (!ctx) throw new Error('usePWAInstallContext must be used within PWAInstallProvider');
  return ctx;
};

export const PWAInstallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const pwaInstall = usePWAInstall();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  
  const [status, setStatus] = useState<PWAInstallStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasCheckedEligibility, setHasCheckedEligibility] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get<{ success: boolean } & PWAInstallStatus>('/pwa-install/status');
      if (response.success) {
        setStatus(response);
      }
    } catch (error) {
      console.error('Failed to fetch PWA install status:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markInstalled = useCallback(async () => {
    if (!user) return;
    
    try {
      await apiClient.post('/pwa-install/mark-installed', {});
      await refreshStatus();
    } catch (error) {
      console.error('Failed to mark PWA installed:', error);
    }
  }, [user, refreshStatus]);

  const claimReward = useCallback(async () => {
    if (!user) return null;
    
    try {
      const response = await apiClient.post<{ 
        success: boolean; 
        gcAmount: number; 
        scAmount: number;
      }>('/pwa-install/claim', {});
      
      if (response.success) {
        await refreshStatus();
        return { gcAmount: response.gcAmount, scAmount: response.scAmount };
      }
      return null;
    } catch (error) {
      console.error('Failed to claim PWA reward:', error);
      return null;
    }
  }, [user, refreshStatus]);

  const dismissInstallPrompt = useCallback(() => {
    notifyDismissed('PWA_INSTALL');
  }, [notifyDismissed]);

  const triggerInstallPrompt = useCallback(() => {
    requestShow('PWA_INSTALL');
  }, [requestShow]);

  // Fetch status on mount and when user changes
  useEffect(() => {
    if (user) {
      refreshStatus();
    } else {
      setStatus(null);
      setHasCheckedEligibility(false);
    }
  }, [user, refreshStatus]);

  // Auto-mark installed when detected running as PWA
  useEffect(() => {
    if (user && pwaInstall.isStandalone && status && !status.pwaInstalled) {
      markInstalled();
    }
  }, [user, pwaInstall.isStandalone, status, markInstalled]);

  // Listen for appinstalled event
  useEffect(() => {
    const handleAppInstalled = () => {
      if (user) {
        markInstalled();
      }
    };

    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, [user, markInstalled]);

  // Show PWA install prompt for eligible users who haven't installed or claimed
  useEffect(() => {
    if (!user || !status || hasCheckedEligibility) return;
    
    // Don't show if:
    // - Already running as PWA
    // - Reward already claimed
    // - Bonus is disabled
    // - Already installed (but can show if canClaim is true to let them claim)
    if (pwaInstall.isStandalone || status.rewardClaimed || !status.enabled) {
      setHasCheckedEligibility(true);
      return;
    }

    // Show prompt if:
    // - User can claim reward (installed but not claimed)
    // - User hasn't installed and we can show install UI
    if (status.canClaim || (!status.pwaInstalled && (pwaInstall.isInstallable || pwaInstall.isIOS))) {
      requestShow('PWA_INSTALL');
      setHasCheckedEligibility(true);
    }
  }, [user, status, pwaInstall.isStandalone, pwaInstall.isInstallable, pwaInstall.isIOS, hasCheckedEligibility, requestShow]);

  const showInstallPrompt = shouldShow('PWA_INSTALL');

  return (
    <PWAInstallContext.Provider
      value={{
        ...pwaInstall,
        status,
        loading,
        refreshStatus,
        markInstalled,
        claimReward,
        showInstallPrompt,
        dismissInstallPrompt,
        triggerInstallPrompt,
      }}
    >
      {children}
    </PWAInstallContext.Provider>
  );
};
